import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { VehicleTiresTab } from "../features/tires/VehicleTiresTab";
import { TabBar } from "../features/tires/tire-ui";
import { api } from "../lib/api";
import { fmtBRL, fmtPct, fmtDateShort, fmtDateTime } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard, type CardStatus } from "../components/KPIStatCard";
import { PageHeader } from "../components/PageHeader";
import { StatusPill } from "../components/StatusPill";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/LoadingSkeleton";
import { IconAlertTriangle, IconTruck, IconLightbulb } from "../components/icons";
import { PlateRunHistory } from "../features/maintenance/PlateRunHistory";
import { KmTimelinePanel } from "../components/KmTimelinePanel";
import type { TransportVehicleDrilldownDto, VehicleMetricMemory } from "@bi/types";
import { TollCoverageNotice, SEM_EXTRATO_TITLE } from "../components/TollCoverageNotice";

interface VehicleCteRow { serie: string | null; numero: number; issueDate: string; customer: string | null; formattedValue: string }

interface VehicleTollPassage { id: string; occurredAt: string; description: string | null; category: string | null; amount: number; formattedAmount: string; invoiceNumber: string }
interface VehicleTollRouteGroup {
  key: string; metodo: "viagem" | "cte_data" | "sem_vinculo";
  cteSeries: string | null; cteNumber: number | null; originCity: string | null; destinationCity: string | null;
  customer: string | null; from: string | null; to: string | null;
  freightTolls: number | null; paidTolls: number; formattedPaid: string; count: number; passages: VehicleTollPassage[];
}
interface VehicleTollsMemory {
  plate: string; source: "sem_parar" | "nenhum" | "sem_extrato"; total: number; formattedTotal: string; count: number;
  freightTolls: number; groups: VehicleTollRouteGroup[]; basis: { trips: number; freights: number; ctes: number }; invoiceNumbers: string[];
}
interface VehicleCtesResponse { plate: string; count: number; formattedTotal: string; ctes: VehicleCteRow[] }

const fmt2 = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CAT_LABEL: Record<string, string> = {
  combustivel: "Combustível", manutencao: "Manutenção", pneus: "Pneus", pedagio: "Pedágio",
  seguro: "Seguro", ipva: "IPVA", licenciamento: "Licenciamento", financiamento: "Financiamento",
  imobilizado: "Imobilizado", rastreamento: "Rastreamento", beneficios: "Benefícios",
  salario: "Salários", administrativo: "Administrativo", frete: "Frete", multa: "Multas", outros: "Outros",
};
const catLabel = (c: string) => CAT_LABEL[c] ?? c.charAt(0).toUpperCase() + c.slice(1);
const SEV_DOT: Record<string, string> = { critical: "bg-red-500", warning: "bg-amber-500", info: "bg-blue-500" };

function deltaVs(cur: number | null, ref: number | null): { text: string; worse: boolean } | null {
  if (cur == null || ref == null || ref <= 0) return null;
  const dev = cur / ref - 1;
  return { text: `${dev >= 0 ? "+" : ""}${Math.round(dev * 100)}% vs. frota`, worse: dev > 0 };
}

/** Card de KPI clicável: abre/fecha a memória de cálculo daquela métrica. */
function MetricCardButton({ metricKey, open, onToggle, children }: {
  metricKey: string; open: string | null;
  onToggle: (k: string | null) => void; children: ReactNode;
}) {
  const isOpen = open === metricKey;
  return (
    <button
      type="button"
      onClick={() => onToggle(isOpen ? null : metricKey)}
      aria-expanded={isOpen}
      title="Ver a memória de cálculo deste indicador"
      className={`text-left focus:outline-none rounded-xl transition ${isOpen ? "ring-2 ring-tm-500" : "hover:opacity-90"}`}
    >
      {children}
    </button>
  );
}

const CALC_TYPE_LABEL: Record<string, string> = {
  realized: "Realizado (documentos do período)",
  estimated: "Estimado (inclui parcela calculada)",
  derived: "Derivado (razão entre dois números da tela)",
};

/** Painel de memória de cálculo de um KPI: fórmula → parcelas → fonte → limitações. */
function MetricMemoryPanel({ memory, periodLabel, onClose }: {
  memory: VehicleMetricMemory; periodLabel: string; onClose: () => void;
}) {
  const anchor = memory.detail?.kind === "fuel" ? "abastecimentos"
    : memory.detail?.kind === "maintenance" ? "manutencoes" : null;
  return (
    <div className="card p-5 border-l-4 border-tm-500">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Memória de cálculo · {memory.label}</p>
          <p className="text-xs text-gray-400 mt-0.5">{periodLabel} · {memory.formula}</p>
        </div>
        <button className="text-xs text-gray-400 hover:text-gray-700" onClick={onClose}>fechar ✕</button>
      </div>

      <div className="space-y-1.5 mb-4">
        {memory.components.map((c, i) => (
          <div key={i} className="flex justify-between items-baseline gap-4 text-sm">
            <span className={`${c.label.startsWith("=") ? "font-semibold text-gray-900" : "text-gray-600"}`}>
              {c.label}
              {c.hint && <span className="text-xs text-gray-400 ml-2">({c.hint})</span>}
            </span>
            <span className={`${c.label.startsWith("=") ? "font-semibold text-gray-900" : "font-medium text-gray-800"} whitespace-nowrap`}>
              {c.value}
            </span>
          </div>
        ))}
      </div>

      {anchor && memory.detail && (
        <p className="text-sm mb-3">
          <a href={`#${anchor}`} className="text-tm-700 hover:underline">{memory.detail.label} ↓</a>
        </p>
      )}

      <div className="text-xs text-gray-500 space-y-1 pt-3 border-t border-surface-border">
        <p><strong>Campo de data:</strong> {memory.dateFieldLabel} · <strong>Tipo:</strong> {CALC_TYPE_LABEL[memory.calculationType] ?? memory.calculationType}</p>
        <p><strong>Fonte:</strong> {memory.sourceTables.join(" · ")}</p>
        {memory.limitations.map((l, i) => <p key={i}>• {l}</p>)}
      </div>
    </div>
  );
}

const METODO_LABEL: Record<VehicleTollRouteGroup["metodo"], { title: string; hint: string }> = {
  viagem: { title: "por viagem", hint: "a passagem caiu na janela saída → chegada da viagem; o frete é o de maior data até o instante da passagem" },
  cte_data: { title: "por data do CT-e", hint: "sem viagem na base: CT-e da placa emitido até 7 dias antes da passagem — chute informado, não prova de rota" },
  sem_vinculo: { title: "sem vínculo", hint: "nenhuma viagem nem CT-e da placa cobre o instante da passagem" },
};

function rotuloGrupo(g: VehicleTollRouteGroup): string {
  const cte = g.cteNumber != null ? `CT-e ${g.cteSeries ? `${g.cteSeries}/` : ""}${g.cteNumber}` : null;
  const rota = g.originCity || g.destinationCity ? `${g.originCity ?? "?"} → ${g.destinationCity ?? "?"}` : null;
  if (g.metodo === "sem_vinculo") return "Sem viagem / CT-e correspondente";
  return [cte, rota, g.customer].filter(Boolean).join(" · ");
}

/**
 * Memória do pedágio: as passagens que compõem o valor do custo direto,
 * agrupadas por rota. O método de atribuição vai junto do grupo — um número
 * atribuído "por data do CT-e" não tem a confiança de um "por viagem".
 */
function TollMemoryPanel({ mem, loading, onClose }: { mem: VehicleTollsMemory | null; loading: boolean; onClose: () => void }) {
  const [aberto, setAberto] = useState<string | null>(null);
  return (
    <div className="card overflow-hidden border-l-4 border-tm-500">
      <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">Memória de cálculo · Pedágio</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {loading || !mem ? "carregando…"
              : mem.source === "sem_parar"
                ? `${mem.count} passagem(ns) · ${mem.formattedTotal} pagos na praça (extrato Sem Parar ${mem.invoiceNumbers.join(", ")})`
                : mem.source === "sem_extrato"
                  ? "sem extrato Sem Parar importado para o período — valor desconhecido"
                  : "período coberto pelo extrato e nenhuma passagem desta placa"}
          </p>
        </div>
        <button className="text-xs text-gray-400 hover:text-gray-700" onClick={onClose}>fechar ✕</button>
      </div>

      {loading || !mem ? (
        <p className="px-5 py-4 text-sm text-gray-400">Carregando passagens…</p>
      ) : mem.groups.length === 0 ? (
        <p className="px-5 py-6 text-sm text-gray-400 text-center">
          {mem.source === "sem_extrato"
            ? "Importe o extrato Sem Parar do período em Frota › Pedágio para ver as passagens."
            : "Nenhuma passagem no período."}
        </p>
      ) : (
        <div className="divide-y divide-gray-100">
          {mem.groups.map((g) => {
            const isOpen = aberto === g.key;
            const diff = g.freightTolls != null && g.freightTolls > 0 ? g.paidTolls - g.freightTolls : null;
            return (
              <div key={g.key}>
                <button
                  type="button"
                  onClick={() => setAberto(isOpen ? null : g.key)}
                  className="w-full px-5 py-3 flex items-center gap-4 text-left hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{rotuloGrupo(g)}</p>
                    <p className="text-xs text-gray-400">
                      <span title={METODO_LABEL[g.metodo].hint}>{METODO_LABEL[g.metodo].title}</span>
                      {g.from && g.to && ` · ${fmtDateShort(g.from)} → ${fmtDateShort(g.to)}`}
                      {` · ${g.count} passagem(ns)`}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900">{g.formattedPaid}</p>
                    {g.freightTolls != null && (
                      <p className={`text-xs ${diff != null && Math.abs(diff) > 0.005 ? (diff > 0 ? "text-amber-600" : "text-tm-700") : "text-gray-400"}`}>
                        cobrado no frete {fmtBRL(g.freightTolls)}
                        {diff != null && ` · ${diff > 0 ? "+" : ""}${fmtBRL(diff)}`}
                      </p>
                    )}
                  </div>
                  <span className="text-gray-300 text-xs">{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && (
                  <div className="overflow-x-auto bg-gray-50/60">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-400">
                          <th className="px-5 py-1.5 text-left font-medium">Data/hora</th>
                          <th className="px-3 py-1.5 text-left font-medium">Concessionária · praça</th>
                          <th className="px-3 py-1.5 text-center font-medium">Cat.</th>
                          <th className="px-5 py-1.5 text-right font-medium">Valor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {g.passages.map((t) => (
                          <tr key={t.id} className="text-gray-700">
                            <td className="px-5 py-1.5 whitespace-nowrap">{fmtDateTime(t.occurredAt)}</td>
                            <td className="px-3 py-1.5">{t.description ?? "—"}</td>
                            <td className="px-3 py-1.5 text-center text-gray-500">{t.category ?? "—"}</td>
                            <td className={`px-5 py-1.5 text-right font-medium ${t.amount < 0 ? "text-tm-700" : ""}`}>{t.formattedAmount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mem && mem.groups.length > 0 && (
        <p className="px-5 py-3 text-xs text-gray-400 border-t border-surface-border">
          Base para atribuir: {mem.basis.trips} viagem(ns), {mem.basis.freights} frete(s) de viagem e {mem.basis.ctes} CT-e da placa no período.
          Passagens em hora local do extrato. "por viagem" é o vínculo confiável; "por data do CT-e" é aproximação.
        </p>
      )}
    </div>
  );
}

type RaioXAba = "visao" | "pneus";

function RaioXTabs({ aba, onChange }: { aba: RaioXAba; onChange: (a: RaioXAba) => void }) {
  return (
    <div className="-mt-2">
      <TabBar<RaioXAba> tabs={[{ key: "visao", label: "Visão do veículo" }, { key: "pneus", label: "Pneus" }]} active={aba} onChange={onChange} />
    </div>
  );
}

export function VehicleDrilldownPage() {
  const { plate = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const aba: RaioXAba = params.get("aba") === "pneus" ? "pneus" : "visao";
  const setAba = (a: RaioXAba) => {
    const p = new URLSearchParams(params);
    if (a === "visao") p.delete("aba"); else p.set("aba", a);
    setParams(p, { replace: true });
  };
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading } = useQuery({
    queryKey: ["transport", "vehicle-drilldown", plate, from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportVehicleDrilldownDto>(
        `/dashboards/transport/vehicle/${encodeURIComponent(plate)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
    enabled: plate.length >= 5,
  });

  // Memória de cálculo: qualquer KPI abre fórmula + componentes + limitações; o card de
  // faturamento também traz os DOCUMENTOS (lista de CT-e da placa no período).
  const [openMetric, setOpenMetric] = useState<string | null>(null);
  const showCtes = openMetric === "revenue";
  const showTolls = openMetric === "tolls";
  // O card de KM abre a linha do tempo: os trechos que explicam (ou não) o número.
  const showKmTimeline = openMetric === "km";
  const { data: cteList, isLoading: cteLoading } = useQuery({
    queryKey: ["transport", "vehicle-ctes", plate, from, to],
    queryFn: () =>
      api.get<VehicleCtesResponse>(
        `/dashboards/transport/vehicle/${encodeURIComponent(plate)}/ctes?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ).catch(() => null),
    staleTime: 60_000,
    enabled: showCtes && plate.length >= 5,
  });

  const { data: tollMem, isLoading: tollLoading } = useQuery({
    queryKey: ["transport", "vehicle-tolls", plate, from, to],
    queryFn: () =>
      api.get<VehicleTollsMemory>(
        `/dashboards/transport/vehicle/${encodeURIComponent(plate)}/tolls?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: showTolls && plate.length >= 5,
  });


  // Aba "Pneus": não depende do período nem de haver CT-e/abastecimento — o mapa
  // físico do veículo existe mesmo num mês sem movimento.
  if (aba === "pneus") {
    return (
      <div className="space-y-6">
        <PageHeader title={`Frota · Raio-X ${plate.toUpperCase()}`} subtitle="Pneus: configuração, mapa dos eixos e ações por posição" />
        <RaioXTabs aba={aba} onChange={setAba} />
        <VehicleTiresTab plate={plate} />
      </div>
    );
  }

  if (isLoading) return <PageSkeleton />;

  if (!data || !data.found) {
    return (
      <div className="space-y-6">
        <PageHeader title={`Frota · Raio-X ${plate.toUpperCase()}`} subtitle="Visão única do veículo no período" />
        <RaioXTabs aba={aba} onChange={setAba} />
        <EmptyState
          icon={<IconTruck className="w-7 h-7" />}
          title="Sem dados para esta placa no período"
          description="Nenhum CT-e, abastecimento, manutenção ou pneu encontrado. Ajuste o período no filtro global ou verifique a placa."
        />
        <p className="text-sm text-gray-400"><Link to="/frota/margem" className="text-tm-700 hover:underline">← Voltar para Margem por Placa</Link></p>
      </div>
    );
  }

  const d = data;
  const r = d.result;
  const openMemory = d.metricMemories.find((m) => m.key === openMetric) ?? null;
  const kmlVsFleet = deltaVs(d.operation.kmPerLiter, r.fleet.kmPerLiter);
  const cpkVsFleet = deltaVs(r.costPerKm, r.fleet.costPerKm);
  const directParts = [
    { label: "Combustível", value: d.costs.direct.fuel },
    { label: "Manutenção", value: d.costs.direct.maintenance },
    { label: "Motorista", value: d.costs.direct.driverPay + d.costs.direct.commission },
    { label: "Pedágio", value: d.costs.direct.tolls },
    { label: "Despesas de viagem", value: d.costs.direct.tripExpenses },
    { label: d.costs.direct.seguroIsReal ? "Seguro" : "Seguro (estimado)", value: d.costs.direct.seguro },
  ];
  const marginStatus: CardStatus =
    r.marginPct == null ? "neutral" : r.marginPct >= 0.12 ? "good" : r.marginPct >= 0.06 ? "warning" : "critical";

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Frota · Raio-X ${d.plate}`}
        subtitle={`${d.driver ? `Motorista: ${d.driver} · ` : ""}${d.rig.trailers.length > 0 ? `Conjunto com ${d.rig.trailers.join(" e ")} · ` : ""}${d.period.label} · comparações ${d.comparisonLabel}`}
        actions={<StatusPill status={r.status} />}
      />
      <RaioXTabs aba={aba} onChange={setAba} />
      {aba === "visao" && <TollCoverageNotice coverage={d.tollCoverage} />}

      {/* Placa de CARRETA: os números dela vivem no cavalo do conjunto. */}
      {d.rig.consolidatedInto && (
        <div className="card p-4 text-sm text-gray-700 border-l-4 border-l-blue-400">
          <p>
            <strong>{d.plate}</strong> é uma <strong>carreta</strong>: não emite CT-e nem abastece — o custo dela
            está consolidado no cavalo que a puxa.
          </p>
          <Link to={`/frota/placa/${encodeURIComponent(d.rig.consolidatedInto)}`} className="text-tm-700 hover:underline">
            Ver o conjunto em {d.rig.consolidatedInto} →
          </Link>
        </div>
      )}

      {/* Conjunto: quanto do custo desta linha veio da carreta. */}
      {d.rig.trailers.length > 0 && d.rig.trailerCost > 0 && (
        <div className="card p-4 text-sm text-gray-700 border-l-4 border-l-blue-400">
          Deste custo direto, <strong>{fmtBRL(d.rig.trailerCost)}</strong> vêm da(s) carreta(s){" "}
          {d.rig.trailers.map((t, i) => (
            <span key={t}>
              {i > 0 && " · "}
              <Link to={`/frota/placa/${encodeURIComponent(t)}`} className="text-tm-700 hover:underline">{t}</Link>
            </span>
          ))}{" "}
          — vínculo do cadastro Sinersoft / reboque da viagem.
        </div>
      )}

      {/* Odômetro de telemetria — CREDITANDO a fonte, ao lado do que vem do
          abastecimento. Não substitui nada: o KM Rodado abaixo continua sendo o
          do abastecimento, e os dois números convivem à vista. */}
      {d.telemetryOdometer && (
        <div className="card p-4 text-sm text-gray-700 border-l-4 border-l-emerald-400">
          <span className="font-semibold text-gray-900">
            Odômetro {Math.round(d.telemetryOdometer.km).toLocaleString("pt-BR")} km
          </span>{" "}
          <span className="text-xs text-gray-500">
            — leitura de rastreador ({d.telemetryOdometer.source}) em{" "}
            {new Date(d.telemetryOdometer.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
          </span>
          <p className="text-xs text-gray-500 mt-1">
            Independente do hodômetro do abastecimento, que é digitado no posto. O KM Rodado do
            período abaixo continua vindo do abastecimento — os dois aparecem juntos de propósito.
          </p>
        </div>
      )}

      {/* AUDITORIA DE KM — abastecimento × odômetro da telemetria, lado a lado.
          Não substitui nada: os dois números aparecem com a fonte declarada, que
          é o ponto de existir esta seção. */}
      {d.kmAudit && (
        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Auditoria de KM · abastecimento × telemetria ({d.kmAudit.source})
          </p>
          <p className="text-xs text-gray-500 mb-4">
            O KM do abastecimento vem do hodômetro digitado no posto. O da telemetria é a
            diferença de odômetro lida pelo rastreador no período.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">KM · abastecimento</p>
              <p className="text-lg font-semibold text-gray-900">
                {d.kmAudit.fuelKm != null ? Math.round(d.kmAudit.fuelKm).toLocaleString("pt-BR") : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">KM · telemetria</p>
              <p className="text-lg font-semibold text-gray-900">
                {d.kmAudit.telemetryKm != null ? Math.round(d.kmAudit.telemetryKm).toLocaleString("pt-BR") : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Diferença</p>
              <p className={`text-lg font-semibold ${
                d.kmAudit.diffKm == null ? "text-gray-400"
                  : Math.abs(d.kmAudit.diffPct ?? 0) > 0.1 ? "text-amber-600" : "text-gray-900"}`}>
                {d.kmAudit.diffKm != null
                  ? `${d.kmAudit.diffKm > 0 ? "+" : ""}${Math.round(d.kmAudit.diffKm).toLocaleString("pt-BR")}`
                  : "—"}
                {d.kmAudit.diffPct != null && (
                  <span className="text-xs font-normal text-gray-500">
                    {" "}({(d.kmAudit.diffPct * 100).toFixed(1)}%)
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">km/l · abast. → telemetria</p>
              <p className="text-lg font-semibold text-gray-900">
                {d.kmAudit.kmPerLiterFuel != null ? d.kmAudit.kmPerLiterFuel.toFixed(2) : "—"}
                {" → "}
                {d.kmAudit.kmPerLiterTelemetry != null ? d.kmAudit.kmPerLiterTelemetry.toFixed(2) : "—"}
              </p>
            </div>
          </div>

          {/* Cobertura: sem isto, um km menor por falta de leitura pareceria
              menos rodagem. */}
          {d.kmAudit.coverage && (
            <p className="text-xs text-gray-500 mt-4">
              Telemetria: {d.kmAudit.coverage.readings} leitura(s) de{" "}
              {new Date(d.kmAudit.coverage.firstAt).toLocaleDateString("pt-BR")} a{" "}
              {new Date(d.kmAudit.coverage.lastAt).toLocaleDateString("pt-BR")} —{" "}
              {Math.round(d.kmAudit.coverage.coversPct * 100)}% do período.
            </p>
          )}

          {d.kmAudit.status === "cobertura_parcial" && (
            <p className="text-xs text-amber-700 mt-2">
              ⚠ A telemetria cobriu só parte do período, então o KM dela está menor por falta de
              leitura — não por menos rodagem. A diferença acima <strong>não</strong> deve ser lida
              como divergência.
            </p>
          )}
          {d.kmAudit.status === "divergencia_alta" && (
            <p className="text-xs text-amber-700 mt-2">
              ⚠ Divergência acima de 10% com cobertura boa da telemetria. Vale conferir os
              hodômetros digitados nos abastecimentos do período.
            </p>
          )}
          {d.kmAudit.status === "sem_telemetria" && (
            <p className="text-xs text-gray-500 mt-2">
              Sem leitura de telemetria no período — só o lado do abastecimento está disponível.
            </p>
          )}
        </div>
      )}

      {/* AUDITORIA DE PEDÁGIO — pago na praça × cobrado no frete. São grandezas
          diferentes; nenhuma substitui a outra, e a diferença é o achado. */}
      {d.tollAudit && (
        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Auditoria de pedágio · pago × cobrado no frete
          </p>
          <p className="text-xs text-gray-500 mb-4">
            O pago vem do extrato {d.tollAudit.source} importado. O cobrado é o pedágio do frete
            no ERP. Um é despesa efetiva, o outro é o que foi contratado.
            {d.tollAudit.paidInPeriodCount > 0 && (
              <> No custo direto desta placa entrou o <strong>pago no período da tela</strong>:{" "}
              {fmtBRL(d.tollAudit.paidInPeriod)} em {d.tollAudit.paidInPeriodCount} passagem(ns).</>
            )}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Pago ({d.tollAudit.source})</p>
              <p className="text-lg font-semibold text-gray-900">{fmtBRL(d.tollAudit.paidTolls)}</p>
              <p className="text-xs text-gray-500">{d.tollAudit.tollCount} passagem(ns)</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Cobrado no frete</p>
              <p className={`text-lg font-semibold ${d.tollAudit.freightTolls ? "text-gray-900" : "text-gray-400"}`}>
                {d.tollAudit.freightTolls ? fmtBRL(d.tollAudit.freightTolls) : "—"}
              </p>
              {!d.tollAudit.freightTolls && (
                <p className="text-xs text-gray-500">sem frete de viagem com pedágio nesta placa no período</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500">Diferença</p>
              <p className={`text-lg font-semibold ${
                d.tollAudit.diffValue == null ? "text-gray-400"
                  : Math.abs(d.tollAudit.diffPct ?? 0) > 0.1 ? "text-amber-600" : "text-gray-900"}`}>
                {d.tollAudit.diffValue != null
                  ? `${d.tollAudit.diffValue > 0 ? "+" : ""}${fmtBRL(d.tollAudit.diffValue)}`
                  : "—"}
                {d.tollAudit.diffPct != null && (
                  <span className="text-xs font-normal text-gray-500">
                    {" "}({(d.tollAudit.diffPct * 100).toFixed(1)}%)
                  </span>
                )}
              </p>
              {d.tollAudit.diffValue == null && (
                <p className="text-xs text-gray-500">sem base de comparação</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500">Fatura(s)</p>
              <p className="text-sm font-medium text-gray-900">{d.tollAudit.invoiceNumbers.join(", ")}</p>
            </div>
          </div>

          {/* O ciclo da fatura não é mês-calendário. Sem dizer o período real, a
              diferença acima pareceria divergência quando é só recorte. */}
          {d.tollAudit.invoicePeriod && (
            <p className="text-xs text-gray-500 mt-4">
              Período coberto pela fatura:{" "}
              {new Date(d.tollAudit.invoicePeriod.from).toLocaleDateString("pt-BR", { timeZone: "UTC" })} a{" "}
              {new Date(d.tollAudit.invoicePeriod.to).toLocaleDateString("pt-BR", { timeZone: "UTC" })}.
            </p>
          )}
          {d.tollAudit.periodMismatch && (
            <p className="text-xs text-amber-700 mt-2">
              ⚠ O ciclo da fatura não coincide com o período da tela. A diferença acima inclui esse
              descasamento — não deve ser lida como divergência de valor.
            </p>
          )}
        </div>
      )}

      {/* Alertas ativos */}
      {d.alerts.length > 0 && (
        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <IconAlertTriangle className="w-4 h-4 text-amber-500" />
            Alertas desta placa ({d.alerts.length})
          </p>
          <div className="space-y-3">
            {d.alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEV_DOT[a.severity]}`} />
                <div className="text-sm">
                  <p className="font-medium text-gray-800">{a.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Evidência: {a.evidence}</p>
                  {a.recommendation && <p className="text-xs text-tm-700 mt-0.5">Ação sugerida: {a.recommendation}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPIs — todos abrem a memória de cálculo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCardButton metricKey="revenue" open={openMetric} onToggle={setOpenMetric}>
          <KPIStatCard label="Faturamento" value={fmtBRL(d.production.revenue)}
            subtitle={`${d.production.cteCount} CT-e(s) · clique para ver`} status="neutral" />
        </MetricCardButton>
        <MetricCardButton metricKey="km" open={openMetric} onToggle={setOpenMetric}>
          <KPIStatCard label="KM Rodado" value={d.production.km > 0 ? Math.round(d.production.km).toLocaleString("pt-BR") : "—"}
            subtitle={d.production.km > 0 ? "clique para ver a linha do tempo" : "sem km no período"} status="neutral" />
        </MetricCardButton>
        <MetricCardButton metricKey="margin" open={openMetric} onToggle={setOpenMetric}>
          <KPIStatCard label="Margem Operacional" value={fmtBRL(r.margin)}
            valueSub={r.marginPct != null ? fmtPct(r.marginPct * 100) : undefined}
            subtitle="margem parcial (custos diretos)" status={marginStatus} />
        </MetricCardButton>
        <MetricCardButton metricKey="cost_per_km" open={openMetric} onToggle={setOpenMetric}>
          <KPIStatCard label="Custo por KM" value={r.costPerKm != null ? `R$ ${fmt2(r.costPerKm)}` : "—"}
            subtitle={cpkVsFleet?.text ?? (r.fleet.costPerKm != null ? `frota: R$ ${fmt2(r.fleet.costPerKm)}` : "sem base")}
            status={cpkVsFleet ? (cpkVsFleet.worse ? "critical" : "good") : "neutral"} />
        </MetricCardButton>
        <MetricCardButton metricKey="km_per_liter" open={openMetric} onToggle={setOpenMetric}>
          <KPIStatCard label="Consumo (km/l diesel)" value={d.operation.kmPerLiter != null ? fmt2(d.operation.kmPerLiter) : "—"}
            subtitle={
              d.operation.kmPerLiter == null ? "sem abastecimento diesel"
              : !d.operation.consumptionReliable ? `base insuficiente (${d.operation.dieselRefuels} abast.)`
              : kmlVsFleet?.text ?? ""
            }
            status={d.operation.consumptionReliable && kmlVsFleet ? (kmlVsFleet.worse ? "good" : "critical") : "neutral"} />
        </MetricCardButton>
        <MetricCardButton metricKey="revenue_per_km" open={openMetric} onToggle={setOpenMetric}>
          <KPIStatCard label="Receita por KM" value={d.production.revenuePerKm != null ? `R$ ${fmt2(d.production.revenuePerKm)}` : "—"}
            subtitle={r.fleet.revenuePerKm != null ? `frota: R$ ${fmt2(r.fleet.revenuePerKm)}` : ""} status="neutral" />
        </MetricCardButton>
      </div>

      {/* Memória de cálculo do KPI aberto (fórmula, parcelas, fonte e limitações) */}
      {openMemory && (
        <MetricMemoryPanel memory={openMemory} periodLabel={d.period.label} onClose={() => setOpenMetric(null)} />
      )}

      {/* Drill do KM: a linha do tempo operacional que explica o número do card */}
      {showKmTimeline && (
        <KmTimelinePanel plate={plate} from={from} to={to} onClose={() => setOpenMetric(null)} />
      )}

      {/* Drill do pedágio: as passagens que compõem o valor, por rota / CT-e */}
      {showTolls && (
        <TollMemoryPanel mem={tollMem ?? null} loading={tollLoading} onClose={() => setOpenMetric(null)} />
      )}

      {/* Drill do faturamento: lista dos CT-e do veículo no período */}
      {showCtes && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">CT-e do veículo no período</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {cteList ? `${cteList.count} CT-e · ${cteList.formattedTotal}` : "carregando…"} · autorizados (status 2), por emissão
              </p>
            </div>
            <button className="text-xs text-gray-400 hover:text-gray-700" onClick={() => setOpenMetric(null)}>fechar ✕</button>
          </div>
          {cteLoading ? (
            <p className="px-5 py-4 text-sm text-gray-400">Carregando CT-e…</p>
          ) : !cteList || cteList.ctes.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Nenhum CT-e autorizado no período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-gray-400 text-xs">
                    <th className="px-5 py-2 text-left font-medium">CT-e</th>
                    <th className="px-3 py-2 text-left font-medium">Emissão</th>
                    <th className="px-3 py-2 text-left font-medium">Cliente</th>
                    <th className="px-5 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cteList.ctes.map((c, i) => (
                    <tr key={`${c.serie}-${c.numero}-${i}`} className="text-gray-700 hover:bg-gray-50">
                      <td className="px-5 py-2 font-mono text-gray-500">{c.serie ? `${c.serie}/` : ""}{c.numero}</td>
                      <td className="px-3 py-2">{fmtDateShort(c.issueDate)}</td>
                      <td className="px-3 py-2 max-w-[280px] truncate" title={c.customer ?? ""}>{c.customer ?? "—"}</td>
                      <td className="px-5 py-2 text-right font-medium">{c.formattedValue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Leitura executiva */}
      {d.reading.length > 0 && (
        <div className="card p-5 border-l-4 border-tm-500">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <IconLightbulb className="w-4 h-4 text-tm-600" />
            Leitura executiva
          </p>
          <div className="space-y-2">
            {d.reading.map((line, i) => (
              <p key={i} className="text-sm text-gray-700">{line}</p>
            ))}
          </div>
        </div>
      )}

      {/* Custos: direto × financeiro + comparação com período anterior */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 mb-1">Custo direto (margem)</p>
          <p className="text-xs text-gray-400 mb-3">Composição usada na margem operacional</p>
          <div className="space-y-2.5">
            {directParts.map((c) => (
              c.label === "Pedágio" ? (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => setOpenMetric(showTolls ? null : "tolls")}
                  title="Ver as passagens que compõem este valor, por rota / CT-e"
                  className={`w-full flex justify-between text-sm rounded px-1 -mx-1 ${showTolls ? "bg-tm-50 ring-1 ring-tm-300" : "hover:bg-gray-50"}`}
                >
                  <span className="text-tm-700 underline decoration-dotted">{c.label}</span>
                  <span className="font-medium text-gray-800">
                    {d.costs.direct.tollsSource === "sem_extrato"
                      ? <span className="text-xs text-gray-400" title={SEM_EXTRATO_TITLE}>sem extrato</span>
                      : <>{fmtBRL(c.value)}<span className="ml-1 text-[10px] text-tm-700">extrato</span></>}
                  </span>
                </button>
              ) : (
                <div key={c.label} className="flex justify-between text-sm">
                  <span className="text-gray-600">{c.label}</span>
                  <span className="font-medium text-gray-800">{fmtBRL(c.value)}</span>
                </div>
              )
            ))}
            {d.costs.direct.tires > 0 && (
              <div className="flex justify-between text-sm text-gray-400">
                <span>Pneus (fora da margem)</span><span>{fmtBRL(d.costs.direct.tires)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm pt-2 border-t border-surface-border font-semibold text-gray-900">
              <span>Total</span><span>{fmtBRL(d.costs.direct.total)}</span>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 mb-1">Custo financeiro alocado</p>
          <p className="text-xs text-gray-400 mb-3">Rateio de contas a pagar por centro de custo (pagos + abertos)</p>
          {!d.costs.financial.available ? (
            <p className="text-sm text-gray-400">Sem rateio financeiro para esta placa no período.</p>
          ) : (
            <div className="space-y-2.5">
              {d.costs.financial.byCategory.slice(0, 8).map((c) => (
                <div key={c.category} className="flex justify-between text-sm">
                  <span className="text-gray-600">{catLabel(c.category)}</span>
                  <span className="font-medium text-gray-800">{fmtBRL(c.total)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm pt-2 border-t border-surface-border font-semibold text-gray-900">
                <span>Total alocado</span><span>{fmtBRL(d.costs.financial.total)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 mb-1">Período anterior</p>
          <p className="text-xs text-gray-400 mb-3">{d.comparisonLabel}</p>
          {!r.previous.found ? (
            <p className="text-sm text-gray-400">Sem dados desta placa no período anterior.</p>
          ) : (
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Faturamento</span><span className="font-medium">{fmtBRL(r.previous.revenue)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Margem</span>
                <span className={`font-medium ${r.previous.margin >= 0 ? "text-gray-800" : "text-danger-600"}`}>{fmtBRL(r.previous.margin)}{r.previous.marginPct != null ? ` (${fmtPct(r.previous.marginPct * 100)})` : ""}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Custo/KM</span><span className="font-medium">{r.previous.costPerKm != null ? `R$ ${fmt2(r.previous.costPerKm)}` : "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">km/l</span><span className="font-medium">{r.previous.kmPerLiter != null ? fmt2(r.previous.kmPerLiter) : "—"}</span></div>
            </div>
          )}
        </div>
      </div>

      {/* Pneus instalados */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border">
          <p className="text-sm font-semibold text-gray-900">Pneus instalados ({d.operation.tires.length})</p>
          <p className="text-xs text-gray-400 mt-0.5">{d.operation.tiresSource ? `Fonte: ${d.operation.tiresSource}` : "Sem pneus vinculados a esta placa na fonte ativa"}</p>
        </div>
        {d.operation.tires.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-surface-border">
                  <th className="px-5 py-2 text-left font-medium">Nº Fogo</th>
                  <th className="px-3 py-2 text-left font-medium">Marca</th>
                  <th className="px-3 py-2 text-left font-medium">Posição</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Vida</th>
                  <th className="px-3 py-2 text-right font-medium">Sulco (mm)</th>
                  <th className="px-3 py-2 text-right font-medium">Limite (mm)</th>
                  <th className="px-3 py-2 text-right font-medium">Vida usada</th>
                  <th className="px-5 py-2 text-right font-medium">Recap.</th>
                </tr>
              </thead>
              <tbody>
                {d.operation.tires.map((t, i) => {
                  const critical = t.lifeUsedPct != null && t.lifeUsedPct >= 0.9;
                  return (
                    <tr key={i} className="border-b border-surface-border/60 hover:bg-gray-50">
                      <td className="px-5 py-2.5 font-medium text-gray-800">{t.fireNumber ?? "—"}</td>
                      <td className="px-3 py-2.5 text-gray-600">{t.brand ?? "—"}</td>
                      <td className="px-3 py-2.5 text-gray-600">{t.position ?? "—"}</td>
                      <td className="px-3 py-2.5 text-gray-600">{t.status ?? "—"}</td>
                      <td className="px-3 py-2.5 text-gray-600">{t.lifeStage ?? "—"}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{t.currentMm != null ? fmt2(t.currentMm) : "—"}</td>
                      <td className="px-3 py-2.5 text-right text-gray-400">{t.mmSafety != null ? fmt2(t.mmSafety) : "—"}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${critical ? "text-danger-600" : "text-gray-700"}`}>
                        {t.lifeUsedPct != null ? fmtPct(t.lifeUsedPct * 100) : "—"}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-600">{t.recapCount ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Históricos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden scroll-mt-4" id="abastecimentos">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Abastecimentos no período ({d.operation.fuelCount})</p>
            {d.operation.fuelCount > d.operation.fuelHistory.length && (
              <p className="text-xs text-gray-400 mt-0.5">Exibindo os {d.operation.fuelHistory.length} mais recentes.</p>
            )}
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-xs text-gray-400 border-b border-surface-border">
                  <th className="px-5 py-2 text-left font-medium">Data</th>
                  <th className="px-3 py-2 text-left font-medium">Comb.</th>
                  <th className="px-3 py-2 text-right font-medium">Litros</th>
                  <th className="px-3 py-2 text-right font-medium">R$/L</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                  <th className="px-3 py-2 text-right font-medium">Hodômetro</th>
                  <th className="px-5 py-2 text-right font-medium">KM</th>
                </tr>
              </thead>
              <tbody>
                {d.operation.fuelHistory.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-6 text-center text-gray-400">Sem abastecimentos no período.</td></tr>
                ) : d.operation.fuelHistory.map((f, i) => (
                  <tr key={i} className="border-b border-surface-border/60">
                    <td className="px-5 py-2 text-gray-600">{fmtDateShort(f.date)}</td>
                    <td className="px-3 py-2 text-gray-600">{f.fuelType ?? "—"}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{fmt2(f.liters)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{f.pricePerLiter != null ? fmt2(f.pricePerLiter) : "—"}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-800">{fmtBRL(f.total)}</td>
                    {/* Km Inicial → Km Atual, as mesmas colunas da Lista de
                        Abastecimentos do SinerSoft, para conferir linha a linha. */}
                    <td className="px-3 py-2 text-right text-xs text-gray-500 whitespace-nowrap">
                      {f.odometerStart != null && f.odometerEnd != null
                        ? `${f.odometerStart.toLocaleString("pt-BR")} → ${f.odometerEnd.toLocaleString("pt-BR")}`
                        : "—"}
                    </td>
                    {/* Linha que NÃO entrou no KM aparece esmaecida e riscada: o
                        número está na origem, mas não soma — e a coluna precisa
                        fechar com o card. */}
                    <td className={`px-5 py-2 text-right ${f.countedInKm ? "text-gray-600" : "text-gray-300 line-through"}`}
                      title={f.countedInKm ? undefined : "Fora do KM: arla/gasolina ou hodômetro fora da faixa possível"}>
                      {f.km != null ? Math.round(f.km).toLocaleString("pt-BR") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <PlateRunHistory plate={d.plate} />

        <div className="card overflow-hidden scroll-mt-4" id="manutencoes">
          <div className="px-5 py-4 border-b border-surface-border">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-gray-900">Manutenções no período ({d.operation.maintenanceCount})</p>
              <Link
                to={`/frota/manutencao/checklists?plate=${encodeURIComponent(d.plate)}`}
                className="text-xs text-tm-700 hover:underline flex-shrink-0"
              >
                Checklists desta placa →
              </Link>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              OS interna: mão de obra não valorada na origem (peças apenas)
              {d.operation.maintenanceCount > d.operation.maintenanceHistory.length
                ? ` · exibindo as ${d.operation.maintenanceHistory.length} mais recentes`
                : ""}
            </p>
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-xs text-gray-400 border-b border-surface-border">
                  <th className="px-5 py-2 text-left font-medium">Data</th>
                  <th className="px-3 py-2 text-left font-medium">Origem</th>
                  <th className="px-3 py-2 text-right font-medium">Peças</th>
                  <th className="px-3 py-2 text-right font-medium">Mão de obra</th>
                  <th className="px-5 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {d.operation.maintenanceHistory.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">Sem manutenções no período.</td></tr>
                ) : d.operation.maintenanceHistory.map((m, i) => (
                  <tr key={i} className="border-b border-surface-border/60">
                    <td className="px-5 py-2 text-gray-600">{fmtDateShort(m.date)}</td>
                    <td className="px-3 py-2 text-gray-600">{m.origin === "internal" ? "Oficina própria" : "Terceiros"}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmtBRL(m.parts)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{m.origin === "internal" && m.labor === 0 ? "n/d" : fmtBRL(m.labor)}</td>
                    <td className="px-5 py-2 text-right font-medium text-gray-800">{fmtBRL(m.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Qualidade dos dados + fontes */}
      <div className="card p-5">
        {d.dataQuality.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-amber-700 mb-1.5">Avisos de qualidade dos dados</p>
            {d.dataQuality.map((q, i) => (
              <p key={i} className="text-xs text-gray-500">• {q}</p>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400">
          Margem <strong>parcial</strong>: custos diretos (combustível + manutenção + motorista + pedágio + despesas de viagem + seguro{d.costs.direct.seguroIsReal ? "" : " estimado"}); não inclui pneus, IPVA/licenciamento, financiamento e custos administrativos. Motorista, pedágio e seguro vêm das viagens do período.
          {" "}Fontes e última sincronização: {d.sources.map((s) => `${s.name}${s.lastSyncAt ? ` (${fmtDateTime(s.lastSyncAt)})` : " (sem registro)"}`).join(" · ")}.
        </p>
      </div>
    </div>
  );
}
